import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Container, Modal, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { Datasets } from '../../api/Datasets';
import { QueryDetails, VizClient } from '../../api/VizClient';
import AssetItem from '../../components/list-items/AssetItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { accessLevelOptions, buildTree, datasourceOptions } from '../../utils';
import { Select } from "antd";

import { BdaDataset } from '../../models/BdaDataset';
import { PagedResults } from '../../models/generics';
import AppBrowsingToolbar, { Filters } from '../../components/common/AppBrowsingToolbar';
import { DefaultOptionType } from 'antd/es/select';
import { EventEmitter } from '../../utils/EventEmitter';
import DatasetPreviewModal from '../../components/generics/DatasetPreviewModal';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import FileSaver from 'file-saver';
import AppLoader from '../../components/common/AppLoader';
import { Datasources } from '../../api/Datasources';
import { DataNode } from 'antd/es/tree';
import { Categories } from '../../api/Categories';

const statusOptions = [
  { label: "Virtual", value: "false", icon: solid("lock") },
  { label: "Created", value: "true", icon: regular("check-circle") },
];

const DEFAULT_FILTERS: Filters = {
  'created': {
    placeholder: 'Select status',
    select: true,
    query: 'created',
    type: 'string',
    options: statusOptions,
    customOptions: true,
    renderOption: (opt: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={opt.value} label={opt.label}>
        <div>
          <FontAwesomeIcon icon={opt.icon} fixedWidth />
          <span className="ms-2">{opt.label}</span>
        </div>
      </Select.Option>
    )
  },
  'accessLevel': {
    placeholder: 'Select privacy level',
    select: true,
    query: 'accessLevel',
    type: 'string',
    options: accessLevelOptions
      .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true),
    customOptions: true,
    renderOption: (option: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={option.value} label={option.label}>
        <div>
          <FontAwesomeIcon icon={option.icon} fixedWidth />
          <span className="ms-2">{option.label}</span>
        </div>
      </Select.Option>
    )
  },
  'datasourceType': {
    placeholder: 'Select Datasource type',
    select: true,
    query: 'datasourceType',
    type: 'string',
    options: [],
  },
  'tags': {
    col: 3,
    placeholder: 'Select  tags...',
    select: true,
    query: 'tags',
    type: 'string',
    options: [],
    selectProps: {
      mode: "tags",
    }
  },
  'owned': { checkbox: true, type: 'boolean', query: 'createdBy' },
  'categories': { placeholder: 'Select categories', treeSelect: true, query: 'categories' },
  'deleted': { checkbox: true, type: 'boolean', query: 'deleted', label: 'Deprecated' },
}

function DatasetsList() {
  const [datasets, setDatasets] = useState<BdaDataset[]>();
  const [totalDatasets, setTotalDatasets] = useState<number>();
  const [error, setError] = useState(false);
  const [latestParams, setLatestParams] = useState<URLSearchParams>();
  const [datasourceTypes, setDatasourceTypes] = useState<string[]>([]);
  const [categoriesOptions, setCategoriesOptions] = useState<DataNode[]>();

  const [datasetToDelete, setDatasetToDelete] = useState<BdaDataset>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [datasetPreview, setDatasetPreview] = useState<any[]>();
  const [show, setShow] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  const assetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasets"));
  const datasetsFilters: Filters = useMemo(() => ({
    ...DEFAULT_FILTERS,
    'search': {
      placeholder: `Search ${assetLabels.singular}...`,
      query: 'freeText',
    },
    'owned': {
      ...DEFAULT_FILTERS.owned!,
      label: `My ${assetLabels.plural}`,
    },
    'categories': {
      ...DEFAULT_FILTERS.categories!,
      treeSelectProps: {
        treeData: categoriesOptions,
        multiple: true,
      },
      col: 3,
    },
    'datasourceType': {
      ...DEFAULT_FILTERS.datasourceType!,
      options: datasourceTypes.map(type => datasourceOptions.find(opt => opt.key === type) || ({ label: type, value: type })),
      customOptions: true,
      renderOption: (option: DefaultOptionType, index: number) => (
        <Select.Option key={index} value={option.value} label={option.label}>
          <div>
            <FontAwesomeIcon icon={option.icon} fixedWidth />
            <span className="ms-2">{option.label}</span>
          </div>
        </Select.Option>
      )
    }
  }), [assetLabels, datasourceTypes, categoriesOptions]);

  useEffect(() => {
    initCategories();
    // eslint-disable-next-line
  }, []);

  const getDatasets = useCallback((queryParams?: Record<string, any>) => {
    const params = new URLSearchParams(queryParams || latestParams);
    if (queryParams) setLatestParams(params);

    Datasets.getDatasets(params)
      .then(
        ({ data: datasetPages }) => {
          setDatasets((datasetPages as PagedResults<BdaDataset>).content);
          setTotalDatasets((datasetPages as PagedResults<BdaDataset>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('datasets-error', `Could not get ${assetLabels.plural}`, `${assetLabels.plural} Error`, 'danger');
          setError(true);
        }
      );
  }, [showToast, latestParams, assetLabels]);

  const reset = useCallback((params?: Record<string, any>) => {
    setError(false);
    setDatasets(undefined);
    getDatasets(params);
  }, [getDatasets]);

  const initDatasourceTypes = useCallback(() => {
    Datasources.getDatasourceTypes()
      .then(
        ({ data }) => setDatasourceTypes(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasource-types-error', message, `Datasource Types Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showToast]);

  useEffect(() => {
    initDatasourceTypes();
  }, [initDatasourceTypes]);

  useEffect(() => {
    EventEmitter.subscribe('browsing:paramsChanged', reset);
    return () => EventEmitter.unsubscribe('browsing:paramsChanged');
  }, [reset]);

  const initCategories = useCallback(() => {
    const params = new URLSearchParams([['populateHierarchy', 'true'], ['target', 'dataset']]);

    Categories.getCategories(params)
      .then(
        ({ data }) => setCategoriesOptions(buildTree(data)),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('categories-error', message, `Categories Error ${status ? `(${status})` : ''}`, 'danger');
          setCategoriesOptions([]);
        }
      )
  }, [showToast]);

  const handleDeleteClose = useCallback(() => {
    setShowDeleteModal(false);
    setDatasetToDelete(undefined);
  }, []);

  const handleClose = useCallback(() => {
    setShow(false);
    setDatasetPreview(undefined);
  }, []);

  const navigateToCreation = useCallback(() => navigate('/datasets/create'), [navigate]);

  const editDataset = useCallback((dataset: BdaDataset) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/datasets/${dataset.id}/edit`);
  }, [navigate]);

  const onDeleteDataset = useCallback((dataset: BdaDataset) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setDatasetToDelete(dataset);
  }, []);

  const deleteDataset = useCallback(() => {
    showLoader();

    Datasets.deleteDataset(datasetToDelete!.id!)
      .then(
        () => {
          showToast('dataset-delete-success', `${assetLabels.singular} deleted successfully.`, 'Success', 'success');
          hideLoader();
          reset();
          handleDeleteClose();
        },
        () => {
          hideLoader();
          showToast('datasets-error', `Could not delete ${assetLabels.singular}`, `${assetLabels.singular} Error`, 'danger');
        }
      )
  }, [assetLabels, datasetToDelete, handleDeleteClose, showLoader, hideLoader, showToast, reset]);

  const datasetDetail = useCallback((dataset: BdaDataset) => () => navigate(`/datasets/${dataset.id}`), [navigate]);

  const openDatasetPreview = useCallback((dataset: BdaDataset) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShow(true);
    const body = new QueryDetails(dataset.datasource, dataset.datasetFileType);

    VizClient.getDatasetPreview(body)
      .then(
        ({ data }) => setDatasetPreview(data || []),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('dataset-error', message, `${assetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast, assetLabels]);

  const downloadDataset = useCallback((dataset: BdaDataset) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    showLoader();
    const body = new QueryDetails(dataset.datasource!, dataset.datasetFileType);

    VizClient.downloadFile(body)
      .then(
        ({ data: datasetToDownload }) => {
          hideLoader();
          const blob = new Blob([datasetToDownload], { type: 'application/zip' });
          FileSaver.saveAs(blob, `${dataset.name}.zip`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasets-error', message, `${assetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showLoader, hideLoader, showToast, assetLabels]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    if (!datasets) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return datasets!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {assetLabels.plural} found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            datasets!.map(dataset => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={dataset.id}>
                <AssetItem
                  type="dataset"
                  asset={dataset}
                  onEdit={editDataset(dataset)}
                  onDelete={onDeleteDataset(dataset)}
                  onPreview={openDatasetPreview(dataset)}
                  onClick={datasetDetail(dataset)}
                  onDownload={downloadDataset(dataset)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }, [error, datasets, assetLabels, editDataset, onDeleteDataset, openDatasetPreview, datasetDetail, downloadDataset]);

  return (
    <Container fluid="md" className="mt-3">
      <AppBrowsingToolbar
        title={`${assetLabels.plural} List`}
        actions={
          <Button size="sm" variant="success" onClick={navigateToCreation}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Register {assetLabels.singular}</span>
          </Button>
        }
        filters={datasetsFilters}
        total={totalDatasets}
      />

      <hr />

      {renderAsyncContent()}

      <DatasetPreviewModal
        show={show}
        onClose={handleClose}
        preview={datasetPreview}
      />

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {assetLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {assetLabels.singular} <b>{datasetToDelete && datasetToDelete.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteDataset}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default memo(DatasetsList);
